import React, { useCallback, useContext } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Query } from '@material-table/core';
import { Form, MTable } from '../../../../../Components';
import { FormValues } from '../../../../../Components/Form/Form.types';
import { useFormField } from '../../../../../Components/Form/FormField';
import { EditName } from '../EditName';
import { useConfirmation } from '../../../../../Context/ConfirmationContext/ConfirmationContext';
import { AccessLevel, LawPortalFeature, UserContext } from '../../../../../Context/UserContext/UserContext';
import { AptorApi, useAptorApi } from '../../../../../Api';
import { INamedEntity, IGridQueryRequest, OrderDirection } from '../../../../../Api/AptorApi';
import { getConnectedEntitiesString } from '../../../../../Utilities';
import { useStyles } from '../Organization.styles';

interface IProcess extends INamedEntity {
  connections: {
    laws: number;
    requirements: number;
  };
}

function getDeleteDescription(
  entity: IProcess,
  formatter: (descriptor: MessageDescriptor, values?: Record<string, string>) => string,
) {
  let connectionsString = getConnectedEntitiesString(
    {
      name: entity.name,
      laws: entity.connections.laws.toString(),
      requirements: entity.connections.requirements.toString(),
    },
    formatter,
  );
  connectionsString = connectionsString ? connectionsString + ' ' : '';
  return connectionsString + formatter({ id: 'organization.processes.delete.confirmation' });
}

const AddProcessForm = (props: { unitId: number; onComplete: () => void }) => {
  const { formatMessage } = useIntl();
  const { getProcessName } = useContext(UserContext);
  const classes = useStyles();

  const newProcessField = useFormField({
    label: formatMessage({ id: 'organization.processes.addProcess'}, { alias: getProcessName(formatMessage)?.toLowerCase() }),
    name: 'name',
    type: 'text',
    required: true,
    hideRequiredAsterisk: true,
  });

  const handleAddProcess = async (data: FormValues, api: AptorApi, onSuccess: () => void) => {
    await api.createProcess(props.unitId, data);
    onSuccess();
    if (api.abortController?.signal.aborted) {
      return;
    }
    props.onComplete();
    newProcessField.clear();
  };

  return (
    <Form
      className={classes.form}
      singleField={newProcessField}
      submit={handleAddProcess}
      submitLabel={formatMessage({ id: 'form.add' })}
    />
  );
};

export const ProcessList = (props: { unitId: number }) => {
  const { api, abortController } = useAptorApi();
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = React.useRef<any>(null);
  const { formatMessage } = useIntl();
  const { deleteConfirmation } = useConfirmation();
  const { hasAccessToFeature, getProcessName } = useContext(UserContext);
  const canManage = hasAccessToFeature(LawPortalFeature.Manage, AccessLevel.Manage);
  const organizationBasePath = location.pathname.startsWith('/law-portal/settings')
    ? '/law-portal/settings'
    : location.pathname.startsWith('/law-portal/organization')
    ? '/law-portal/organization'
    : '/organization';

  const handleDataFetch = useCallback(
    async (query: Query<IProcess>) => {
      return await api.searchProcesses<IProcess>(props.unitId, {
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'name',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
      } as IGridQueryRequest);
    },
    [api, props.unitId],
  );

  const edit = (process: IProcess) => ({
    tooltip: formatMessage({ id: 'component.mtable.editTooltip' }),
    icon: 'edit',
    onClick: () => navigate(`${organizationBasePath}/company-units/${props.unitId}/processes/${process.id}`),
  });

  const remove = (process: IProcess) => ({
    tooltip: formatMessage({ id: 'component.mtable.deleteTooltip' }),
    icon: 'delete',
    onClick: async () => {
      const newRef: any = tableRef.current;
      const onRemove = async () => {
        await api.removeProcess(props.unitId, process.id);
        if (abortController.current.signal.aborted) {
          return;
        }
        newRef.onQueryChange();
      };
      deleteConfirmation(onRemove, { description: getDeleteDescription(process, formatMessage) });
    },
  });

  const reloadTable = () => {
    (tableRef.current as any).onQueryChange();
  };

  return (
    <>
      {canManage && <AddProcessForm unitId={props.unitId} onComplete={reloadTable} />}
      <MTable
        // hideToolbar
        tableRef={tableRef}
        // columns={[{ title: formatMessage({ id: 'organization.processes' }), field: 'name' }]}
        columns={[{ title: getProcessName(formatMessage), field: 'name' }]}
        data={handleDataFetch}
        actions={canManage ? [edit, remove] : undefined}
        components={{}}
      />
    </>
  );
};

export const EditProcess = () => {
  const { unitId: unitIdParam } = useParams<{ unitId: string; id: string }>();
  const unitId = parseInt(unitIdParam || '0');
  const { api } = useAptorApi();
  const navigate = useNavigate();
  const location = useLocation();
  const organizationBasePath = location.pathname.startsWith('/law-portal/settings')
    ? '/law-portal/settings'
    : location.pathname.startsWith('/law-portal/organization')
    ? '/law-portal/organization'
    : '/organization';

  const fetchProcess = useCallback((id: number) => api.getProcess(unitId, id), [api, unitId]);

  const handleEditProcess = useCallback(
    async (id: number, data: FormValues, api: AptorApi) => {
      await api.updateProcess(unitId, id, data);
      navigate(`${organizationBasePath}/company-units/${unitId}`);
    },
    [navigate, organizationBasePath, unitId],
  );
  return (
    <EditName
      titleKey="organization.processes"
      backToLink={`${organizationBasePath}/company-units/${unitId}`}
      fetcher={fetchProcess}
      submit={handleEditProcess}
    />
  );
};
